import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';

export type ButtonVariant = 'primary' | 'default' | 'text' | 'ghost' | 'danger' | 'soft-danger' | 'soft-info' | 'inverse' | 'accent';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  /** Accepts the ui sizes (sm/md/lg) and antd sizes (small/middle/large). */
  size?: ButtonSize | 'small' | 'middle' | 'large';
  icon?: ReactNode;
  loading?: boolean;
  block?: boolean;
  /** When set, renders an anchor styled as a button (antd Button href parity). */
  href?: string;
  target?: string;
  rel?: string;
  /** antd `type` alias → variant. Ignored if `variant` is given. */
  danger?: boolean;
  /** antd Button shape. `circle` → round icon button; `round` → pill. */
  shape?: 'circle' | 'round' | 'default';
  /** Native button type (antd `htmlType`). */
  type?: 'button' | 'submit' | 'reset';
}

const SIZE_ALIAS: Record<string, ButtonSize> = { small: 'sm', middle: 'md', large: 'lg', sm: 'sm', md: 'md', lg: 'lg' };

const VARIANT: Record<ButtonVariant, string> = {
  // Solid fills force white text (`!`) so a container/reset can never bleed dark body-text through.
  // A solid fill lit from the top (500 -> 600) and a shadow in the button's OWN hue, so the primary
  // action reads as a raised, lit control rather than a flat green rectangle. `bg-brand-600` stays
  // underneath the gradient as the fallback colour; `disabled:bg-none!` in the base class strips the
  // gradient when the button is disabled, otherwise the image would keep painting green over the
  // disabled background-color.
  primary: 'bg-brand-600 bg-linear-to-b from-brand-500 to-brand-600 text-white! shadow-glow hover:from-brand-600 hover:to-brand-700 active:from-brand-700 active:to-brand-800',
  // A solid action that must NOT read as one more green primary — the one different kind of act on a
  // page full of them (Territory Coverage: a temporary stand-in among eight "Assign" buttons). Violet,
  // because green is the brand's forward step, red a problem, gold an owner star and blue a Sales Head.
  accent: 'bg-violet-600 bg-linear-to-b from-violet-500 to-violet-600 text-white! shadow-card hover:from-violet-600 hover:to-violet-700 active:from-violet-700 active:to-violet-800',
  default:
    'bg-surface text-content border border-line hover:border-brand-400 hover:text-brand-700 dark:hover:border-brand-500',
  text: 'bg-transparent text-content hover:bg-surface-2',
  // Soft tinted "secondary action" buttons — light fill, coloured label. Reads far cleaner than a row
  // full of solid green/red blocks, while still carrying an intent colour + text label.
  ghost:
    'bg-brand-50 text-brand-700 hover:bg-brand-100 dark:bg-brand-900/25 dark:text-brand-300 dark:hover:bg-brand-900/40',
  danger: 'bg-danger-500 bg-linear-to-b from-danger-500 to-danger-600 text-white! shadow-glow-danger hover:from-danger-600 hover:to-danger-600',
  // For a CTA sitting ON a solid brand surface (the dashboard hero band): 'primary' is brand-600, so
  // on a green hero it is green-on-green and all but disappears. Invert it — white fill, deep brand
  // label. Colours are fixed on purpose: the hero paints its own green gradient in BOTH themes, so
  // this must NOT follow the light/dark surface tokens. The focus ring is white with a dark-green
  // offset (a plain white ring would vanish against the white fill); '!' beats the base brand ring
  // because cn() is a plain joiner with no tailwind-merge.
  inverse:
    // ui-ok: the hero paints its own green gradient in BOTH themes, so this fill must not follow the surface tokens.
    'bg-white text-brand-800! shadow-card hover:bg-brand-100 active:bg-brand-200 focus-visible:ring-white! focus-visible:ring-offset-2 focus-visible:ring-offset-brand-900',
  'soft-danger':
    'bg-danger-500/10 text-danger-600 hover:bg-danger-500/18 dark:bg-danger-500/15 dark:text-danger-400 dark:hover:bg-danger-500/25',
  /* The third tint, completing the set a row of icon actions needs: `ghost` is the forward step
     (green), `soft-danger` the destructive one (red), and this is the neutral-but-not-passive act —
     amend, reassign, re-open. It belongs in the kit rather than as classes at a call site: `cn()`
     carries no tailwind-merge, so a caller's `bg-*` and the variant's own `bg-*` would resolve by
     stylesheet order and the tint would silently lose. */
  'soft-info':
    'bg-sky-500/12 text-sky-600 hover:bg-sky-500/20 dark:bg-sky-400/15 dark:text-sky-300 dark:hover:bg-sky-400/25',
};

// Heights match the form-control scale (Input/Select): sm 32 / md 36 / lg 44, so a button lines up
// with the input or select beside it in a toolbar/filter row instead of towering 4px over it.
const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 px-2.5 text-sm',
  md: 'h-9 gap-2 px-4 text-sm',
  lg: 'h-11 gap-2 px-5 text-[15px]',
};

/** The primary action control of the Green Bay UI kit (replaces antd Button). */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  // Default size is 'sm' (32px) — the app-wide compact standard. Buttons that must line up with a
  // full-height input/select box opt into size="md" (36px); hero CTAs into size="lg".
  { variant, size = 'sm', icon, loading, block, className, children, disabled, type, href, target, rel, danger, shape, ...rest },
  ref,
) {
  const v: ButtonVariant = variant ?? (danger ? 'danger' : 'default');
  const sz = SIZE_ALIAS[size] ?? 'md';
  const iconOnly = shape === 'circle' || (icon != null && (children == null || children === ''));
  const cls = cn(
    'inline-flex items-center justify-center font-semibold leading-none',
    shape === 'circle' ? 'rounded-full' : shape === 'round' ? 'rounded-pill' : 'rounded-control',
    'transition-colors duration-150 select-none',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
    // `disabled:bg-none!` kills the gradient as well as the fill — without it the solid variants'
    // background-IMAGE keeps painting brand green straight over the disabled background-COLOR, and
    // a disabled primary button looks exactly like an enabled one.
    // The inset ring keeps a disabled button reading as a BUTTON. Grey fill + muted text alone melted into
    // any grey footer (the expense approve dialog's blocked "Approve ₹400" looked like a line of text).
    // A ring, not a border: it takes no space, so nothing shifts between enabled and disabled.
    'disabled:cursor-not-allowed disabled:bg-surface-2! disabled:bg-none! disabled:text-muted! disabled:shadow-none disabled:ring-1 disabled:ring-inset disabled:ring-line-strong',
    VARIANT[v],
    // Icon-only buttons (incl. shape="circle") render as compact squares — no text padding,
    // so row-action clusters stay tight instead of wide rectangles.
    iconOnly
      ? (sz === 'sm' ? 'size-8 px-0' : sz === 'lg' ? 'size-11 px-0' : 'size-9 px-0')
      : SIZE[sz],
    block && 'w-full',
    className,
  );
  const inner = (
    <>
      {loading ? (
        <Spinner className="-ml-0.5" />
      ) : icon ? (
        <span className="grid place-items-center text-[1.15em] leading-none">{icon}</span>
      ) : null}
      {/* leading-normal (not the button's leading-none) so the truncate/overflow-hidden box is tall
          enough to contain glyph descenders (g, p, y) — otherwise they clip at the bottom. */}
      {children != null && children !== '' && <span className="truncate leading-normal">{children}</span>}
    </>
  );
  // antd Button with `href` renders an anchor styled as a button.
  if (href) {
    return (
      <a href={href} target={target} rel={rel} className={cn(cls, disabled && 'pointer-events-none opacity-60')} {...(rest as Record<string, unknown>)}>
        {inner}
      </a>
    );
  }
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || loading}
      className={cls}
      {...rest}
    >
      {inner}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Square size in px (the control is a circle of this diameter). */
  diameter?: number;
}

/** Circular icon-only button (theme toggle, notifications, row actions). */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { diameter = 40, className, children, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cn(
        'inline-grid place-items-center rounded-pill text-[18px] transition-colors duration-150 [&>svg]:size-[18px]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      style={{ width: diameter, height: diameter }}
      {...rest}
    >
      {children}
    </button>
  );
});

/** Brand spinner used by Button.loading and anywhere a small inline loader is needed. */
export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn('h-[1.05em] w-[1.05em] animate-spin', className)}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
